import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError, API } from "@/auth";
import { LHSLogo } from "@/logos";
import { Loader2, UserPlus, ShieldCheck } from "lucide-react";

export default function RegisterPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", password: "", workshop: "" });
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitReg = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, form);
      setInfo("Un code de vérification a été envoyé à " + form.email);
      setStep(2);
    } catch (er) { setErr(formatApiError(er)); }
    finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { email: form.email, code });
      // Now log in (need a captcha)
      const c = await axios.get(`${API}/auth/captcha`);
      await login(form.email, form.password, {
        captcha_a: c.data.a, captcha_b: c.data.b, captcha_answer: c.data.a + c.data.b,
      });
      nav("/paiements");
    } catch (er) { setErr(formatApiError(er)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    setErr(""); setInfo("");
    try {
      await axios.post(`${API}/auth/resend-otp`, { email: form.email });
      setInfo("Nouveau code envoyé à " + form.email);
    } catch (er) { setErr(formatApiError(er)); }
  };

  return (
    <div className="min-h-screen bg-black bg-grid flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><LHSLogo size={64} /></div>
        <div className="card-lhs p-6 sm:p-8" data-testid="register-card">
          {step === 1 && (
            <>
              <h1 className="font-display text-2xl font-extrabold uppercase tracking-wider mb-1">Créer un compte</h1>
              <p className="text-sm text-zinc-400 mb-6">Ouvrez votre espace technicien LHSunlocker</p>
              <form onSubmit={submitReg} className="space-y-3">
                <input data-testid="reg-name" required className="input-lhs" placeholder="Nom d'utilisateur" value={form.name} onChange={upd("name")} />
                <input data-testid="reg-workshop" className="input-lhs" placeholder="Atelier / Boutique (optionnel)" value={form.workshop} onChange={upd("workshop")} />
                <input data-testid="reg-email" required type="email" className="input-lhs" placeholder="Adresse e-mail" value={form.email} onChange={upd("email")} />
                <input data-testid="reg-password" required type="password" minLength={6} className="input-lhs" placeholder="Mot de passe (6+ caractères)" value={form.password} onChange={upd("password")} />
                {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
                <button data-testid="reg-submit" disabled={loading} className="btn-orange w-full rounded-lg py-3 flex items-center justify-center gap-2 uppercase tracking-wider text-sm">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Créer mon compte
                </button>
              </form>
            </>
          )}
          {step === 2 && (
            <>
              <div className="flex items-center gap-2 mb-2 text-orange-400">
                <ShieldCheck className="w-5 h-5" />
                <h1 className="font-display text-xl font-extrabold uppercase tracking-wider">Vérification e-mail</h1>
              </div>
              <p className="text-sm text-zinc-400 mb-4">Saisissez le code à 6 chiffres envoyé à <strong className="text-white">{form.email}</strong></p>
              {info && <div className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 mb-3">{info}</div>}
              <form onSubmit={submitOtp} className="space-y-3">
                <input data-testid="otp-code" required maxLength={6} pattern="[0-9]{6}" inputMode="numeric" className="input-lhs font-mono-tech text-2xl text-center tracking-[.5em]"
                  placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
                <button data-testid="otp-submit" disabled={loading || code.length !== 6} className="btn-orange w-full rounded-lg py-3 flex items-center justify-center gap-2 uppercase tracking-wider text-sm">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Vérifier
                </button>
                <button type="button" onClick={resend} className="w-full text-xs text-zinc-400 hover:text-orange-400 pt-2">Renvoyer le code</button>
              </form>
            </>
          )}
          <div className="mt-6 pt-5 border-t border-zinc-800 text-center">
            <Link data-testid="link-login" to="/login" className="text-sm text-zinc-400 hover:text-orange-500">
              Déjà un compte ? <span className="text-orange-500 font-semibold">Se connecter</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
